import { keyframes } from '@emotion/react';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import { Box, Button, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import PhoneFrame from '../components/PhoneFrame';
import { markExerciseComplete, mockCourses } from '../mockData';
import { ExerciseDestination, getLessonExercises, getLessonSessions, sessionForStep } from '../mockLessonContent';
import { BLUE, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT, yellowButtonSx } from '../studentStyles';
// v2 keeps one set of exercise cards for mobile and desktop: the desktop cards
// render their compact arrangement when given the 'mobile' layout.
import { ReadyState } from '../../studentDesktop/components/exercises/exerciseLayout';
import { defaultReadyFor, renderLessonExercise } from '../../studentDesktop/components/exercises/lessonExerciseRenderer';

const popIn = keyframes`
    from { transform: scale(0.6); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
`;

// Full-screen step player: one exercise per screen, resumed from the first
// incomplete prompt. Lessons are split into practice sessions; finishing one
// offers the next (the v2 home of the legacy "Start Next Lesson" prompt).
export default function StudentLessonPlayer() {
    const history = useHistory();
    const location = useLocation();
    const [destination, setDestination] = React.useState<ExerciseDestination>();
    const { courseId, lessonId } = useParams() as { courseId: string; lessonId: string };

    const course = mockCourses.find(c => c.id === Number(courseId));
    const lesson = course?.lessons.find(l => l.id === Number(lessonId));

    const exercises = React.useMemo(() => (lesson ? getLessonExercises(lesson) : []), [lesson]);
    // Resume at the first incomplete exercise; a finished lesson replays from the start.
    const requestedStep = Number(new URLSearchParams(location.search).get('step'));
    const initialIndex = Number.isInteger(requestedStep) && requestedStep > 0 && requestedStep <= exercises.length
        ? requestedStep - 1 : !lesson || lesson.completedPrompts >= lesson.totalPrompts ? 0 : lesson.completedPrompts;
    const [index, setIndex] = React.useState(initialIndex);
    const [practiceRun, setPracticeRun] = React.useState(0);
    // Review before this visit's entry point is navigable; restarting at zero
    // still requires a fresh attempt without erasing historical progress.
    const reviewBefore = React.useRef(Math.min(initialIndex, lesson?.completedPrompts ?? 0));
    const practicedSteps = React.useRef(new Set<number>());
    const [ready, setReady] = React.useState<ReadyState | null>(() => (defaultReadyFor(exercises[initialIndex]) ? 'done' : null));
    const [sessionComplete, setSessionComplete] = React.useState(false);

    if (!course) {
        return <Redirect to="/mockup/v2/student" />;
    }
    if (!lesson) {
        return <Redirect to={`/mockup/v2/student/course/${course.id}`} />;
    }

    const total = exercises.length;
    const finished = index >= total;
    const incompleteEnd = (finished || sessionComplete) && lesson.completedPrompts < (finished ? total : sessionForStep(lesson, index).end);
    const displayedCount = incompleteEnd ? lesson.completedPrompts : Math.min(index + 1, total);
    const exercise = exercises[index];
    const coursePath = `/mockup/v2/student/course/${course.id}`;
    const lessonIndex = course.lessons.findIndex(l => l.id === lesson.id);
    const nextLesson = course.lessons[lessonIndex + 1];
    const sessions = getLessonSessions(lesson);
    const session = sessionForStep(lesson, Math.min(index, total - 1));
    const nextSession = sessions.find(item => item.number === session.number + 1);

    const goTo = (step: number) => {
        setSessionComplete(false);
        setDestination(undefined);
        setIndex(step);
        setReady(step < reviewBefore.current || practicedSteps.current.has(step) || defaultReadyFor(exercises[step]) ? 'done' : null);
    };

    // A skippable exercise stays skippable until it is actually done.
    const handleReady = (state: ReadyState = 'done', next?: ExerciseDestination) => {
        setDestination(next);
        setReady(prev => (state === 'done' || prev === 'done' ? 'done' : 'skip'));
    };

    const handleContinue = () => {
        practicedSteps.current.add(index);
        if (index <= lesson.completedPrompts) markExerciseComplete(lesson, index);
        if (destination) {
            if (destination.courseId === course.id && destination.lessonId === lesson.id) {
                if (destination.restart) {
                    practicedSteps.current.clear();
                    reviewBefore.current = 0;
                    setPracticeRun(value => value + 1);
                }
                goTo(destination.step - 1);
                return;
            }
            history.push(`/mockup/v2/student/course/${destination.courseId}/lesson/${destination.lessonId}?step=${destination.step}`);
            return;
        }
        const next = index + 1;
        if (nextSession && next === session.end) {
            setSessionComplete(true);
            return;
        }
        goTo(next);
    };

    const celebration = (title: string, detail: string, icon: React.ReactNode) => (
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, textAlign: 'center', py: 4 }}>
            <Box sx={{
                width: 96, height: 96, borderRadius: '50%', backgroundColor: `${GREEN}1A`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: `${popIn} 300ms ease-out`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 24, color: NAVY, mt: 2.5 }}>
                {title}
            </Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE, mt: 0.75 }}>
                {detail}
            </Typography>
        </Stack>
    );

    return (
        <PhoneFrame>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 96px)' }}>

                {/* Top bar: exit + lesson progress */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: sessions.length > 1 && !finished ? 0.5 : 2.5 }}>
                    <IconButton aria-label="Exit lesson" onClick={() => history.push(coursePath)} sx={{ width: 44, height: 44, ml: -1, color: SLATE }}>
                        <CloseRoundedIcon />
                    </IconButton>
                    <LinearProgress
                        variant="determinate"
                        value={incompleteEnd ? lesson.completedPrompts / total * 100 : Math.min(100, (index / total) * 100)}
                        sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: '999px',
                            backgroundColor: RING_TRACK,
                            '& .MuiLinearProgress-bar': { borderRadius: '999px', backgroundColor: BLUE },
                        }}
                    />
                    <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SLATE, whiteSpace: 'nowrap' }}>
                        {displayedCount} of {total}{incompleteEnd ? ' completed' : ''}
                    </Typography>
                </Box>
                {sessions.length > 1 && !finished && (
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', color: SLATE, textAlign: 'right', mb: 2 }}>
                        SESSION {session.number} OF {sessions.length}
                    </Typography>
                )}

                {sessionComplete && nextSession ? (
                    <>
                        {celebration(
                            lesson.completedPrompts >= session.end ? `Session ${session.number} complete!` : 'Session preview finished',
                            lesson.completedPrompts >= session.end ? `Ready for session ${nextSession.number} of ${sessions.length}? It has ${nextSession.end - nextSession.start} activities.` : 'Earlier activities are still unfinished. You can preview the next session or return to your lessons.',
                            lesson.completedPrompts >= session.end ? <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 48 }} /> : <RateReviewRoundedIcon sx={{ color: BLUE, fontSize: 48 }} />,
                        )}
                        <Stack spacing={1} sx={{ mt: 3 }}>
                            <Button fullWidth variant="contained" onClick={() => goTo(nextSession.start)} sx={{ ...yellowButtonSx, py: 1.5 }}>
                                Start session {nextSession.number}
                            </Button>
                            <Button fullWidth onClick={() => history.push(coursePath)} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 14, color: BLUE }}>
                                Stop for now
                            </Button>
                        </Stack>
                    </>
                ) : finished && lesson.completedPrompts < lesson.totalPrompts ? (
                    <>
                        {celebration('Preview finished', 'Earlier activities are still unfinished. Your saved progress is unchanged.', <RateReviewRoundedIcon sx={{ color: BLUE, fontSize: 48 }} />)}
                        <Button fullWidth onClick={() => goTo(lesson.completedPrompts)} sx={yellowButtonSx}>Continue lesson</Button>
                    </>
                ) : finished ? (
                    <>
                        {celebration('Lesson complete!', `You finished ${lesson.name}`, <CelebrationRoundedIcon sx={{ color: GREEN, fontSize: 48 }} />)}
                        <Box sx={{ width: '100%', p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, textAlign: 'center' }}>
                            <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 24 }} />
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY, mt: 0.5 }}>
                                {total} exercises
                            </Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>
                                all done
                            </Typography>
                        </Box>
                        <Stack spacing={1} sx={{ mt: 3 }}>
                            {nextLesson ? (
                                <>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => history.push(`${coursePath}/lesson/${nextLesson.id}`)}
                                        sx={{ ...yellowButtonSx, py: 1.5 }}
                                    >
                                        Start next lesson
                                    </Button>
                                    <Button fullWidth onClick={() => history.push(coursePath)} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 14, color: BLUE }}>
                                        Back to course
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button fullWidth variant="contained" onClick={() => history.push(coursePath)} sx={{ ...yellowButtonSx, py: 1.5 }}>
                                        Back to course
                                    </Button>
                                    <Button fullWidth onClick={() => history.push('/mockup/v2/student')} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 14, color: BLUE }}>
                                        Home
                                    </Button>
                                </>
                            )}
                            <Button
                                fullWidth
                                startIcon={<RateReviewRoundedIcon />}
                                onClick={() => history.push('/mockup/v2/student/feedback')}
                                sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SLATE }}
                            >
                                Teacher feedback
                            </Button>
                        </Stack>
                    </>
                ) : (
                    <>
                        <Box key={`${exercise.id}-${practiceRun}`} sx={{ flex: 1 }}>
                            {renderLessonExercise(exercise, handleReady, 'mobile')}
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                            <Button
                                aria-label="Back to the previous activity"
                                disabled={index === 0}
                                onClick={() => goTo(index - 1)}
                                sx={{ minWidth: 52, borderRadius: '11px', border: `1px solid ${RING_TRACK}`, color: BLUE }}
                            >
                                <ArrowBackRoundedIcon />
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={ready == null}
                                endIcon={<ArrowForwardRoundedIcon />}
                                onClick={handleContinue}
                                sx={{ ...yellowButtonSx, py: 1.5 }}
                            >
                                {ready === 'skip' ? 'Skip' : destination ? 'Continue' : index === total - 1 ? 'Finish lesson' : 'Continue'}
                            </Button>
                        </Stack>
                    </>
                )}
            </Box>
        </PhoneFrame>
    );
}
